import { fetchJSON, merge } from 'omni-ui';
import { mergeURLSearchParams, downloadFile } from '../helpers/utils.js';

/**
 * @memberof CampaignsAPI
 * @const {Object}
 * @readonly
 * @property {string} local - Local Environment URL
 * @property {string} dev - Dev Environment URL
 * @property {string} qa - QA Environment URL
 * @property {string} stg - Staging Environment URL
 * @property {string} prod - Production Environment URL
 */
const originsByEnv = {
  local: 'http://0.0.0.0:5001',
  dev: 'https://devcampaign.api.annalect.com',
  qa: 'https://qacampaign.api.annalect.com',
  stg: 'https://stgcampaign.api.annalect.com',
  prod: 'https://campaign.api.annalect.com',
};

/**
 * @memberof CampaignsAPI
 * @const {Object}
 * @readonly
 */
const defaultGetCampaignsSearchParams = { status: 'active' };

/**
 * Service for calls to the campaign
 * @property {string} token - API JWT
 * @property {string} env - Which environment to make calls to
 * @property {string} origin - Selected environment URL
 * @property {Object} fetchOptions - Options to be passed in `fetch` calls
 * @property {Object} fetchOptions.headers - HTTP headers defaulted, `method` and `body` added as needed
 */
class CampaignsAPI {
  /**
   * @param {string} token - API JWT
   * @param {string} [env='prod'] - Key for {@link #.originsByEnv} to select
   * correct environment to make calls to
   */
  constructor(token, env = 'prod') {
    this.token = token;
    this.env = env;

    this.origin = originsByEnv[env];
    this.fetchOptions = {
      headers: { Authorization: `Bearer ${token}` },
    };
  }

  static cache = {};

  // Helper function to statically cache api calls
  static async getCache(key, generateFn) {
    if (!this.cache[key]) {
      this.cache[key] = generateFn();
    }
    return this.cache[key];
  }

  /**
   * @param {Object} data - Campaign data
   * @returns {Promise}
   */
  createCampaign(data) {
    return fetchJSON(
      `${this.origin}/campaign/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  /**
   * @param {string} clientId - ID for requesting campaigns by client
   * @param {Object} searchParams
   * @returns {Promise}
   */
  getCampaigns(clientId, searchParams) {
    const url = new URL(`/campaign/client/${clientId}/`, this.origin);
    url.search = mergeURLSearchParams(
      defaultGetCampaignsSearchParams,
      searchParams,
    );

    return fetchJSON(url, this.fetchOptions);
  }

  /**
   * @param {string} campaignId
   * @returns {Promise}
   */
  getCampaign(campaignId) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/`,
      this.fetchOptions,
    );
  }

  /**
   * @param {string} campaignId
   * @param {Object} data
   * @returns {Promise}
   */
  saveCampaign(campaignId, data) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/`,
      merge(this.fetchOptions, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    );
  }

  /**
   * @param {string} campaignId
   * @param {Object} data
   * @returns {Promise}
   */
  cloneCampaign(campaignId, data) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/clone`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  /**
   * @param {string} campaignId
   * @returns {Promise}
   */
  async exportCampaign(campaignId, stepId) {
    const url = `${this.origin}/campaign/${campaignId}/export/${
      stepId ? `?uuid_framework_template_step=${stepId}` : ''
    }`;
    const response = await fetch(url, {
      cache: 'no-store',
      ...this.fetchOptions,
    });
    if (!response.ok) throw response;
    downloadFile(response, 'campaign-export.pptx');
  }

  async exportCampaignTemplates(campaignId, templates, overrides) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/export-templates/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify({ templates, overrides }),
      }),
    );
  }

  getCampaignAssets(campaignId, types = []) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/modules/${
        types ? `?module_types=${types.join(',')}` : ''
      }`,
      this.fetchOptions,
    );
  }

  /**
   * @param {String} campaignId - current active uuid_campaign
   * @param {String} frameworkStepId - current active uuid_framework_step
   * @param {Object} user - current logged in user
   * @summary - fetch all the data pertaining to the step and locks the step with the current user if step hasn't been locked by anyone else
   * @returns {Promise} - step data
   */
  getStep(campaignId, frameworkStepId, user) {
    const url = `${this.origin}/step/${campaignId}/${frameworkStepId}/?guid_person=${user.personId}&fname=${user.firstName}&lname=${user.lastName}`;
    return fetchJSON(url, this.fetchOptions);
  }

  /**
   * @param {*} campaignId - current active uuid_campaign
   * @param {*} personId - current logged in user
   * @returns - {"success":true} when unlock is successful
   * @summary - unlocks all steps within a campaign that was locked by the user(personId)
   */
  unlockAllUserSteps(campaignId, personId) {
    const url = `${this.origin}/campaign/${campaignId}/${personId}/unlock/`;
    return fetchJSON(
      url,
      merge(this.fetchOptions, {
        method: 'POST',
      }),
    );
  }

  /**
   * @param {string} campaignStepId
   * @param {string} moduleId
   * @returns {Promise}
   */
  getStepData(campaignStepId, objectId) {
    const url = `${this.origin}/step/${campaignStepId}/${objectId}/saved-data/`;
    return fetchJSON(url, this.fetchOptions);
  }

  /**
   * @param {string} campaignStepId
   * @param {string} objectId
   * @param {string} moduleId
   * @param {Object} data
   * @returns {Promise}
   * @todo Maybe define the data with a typedef?
   */
  saveStepModule(campaignStepId, objectId, moduleId, data) {
    return fetchJSON(
      `${this.origin}/step/instance/${campaignStepId}/module/${moduleId}/${
        objectId ? `${objectId}/` : ''
      }data/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    );
  }

  /**
   * @param {string} campaignId
   * @param {string} campaignStepId - which step triggered the save
   * @param {string} moduleId - which module triggered the save
   * @param {Object} data - dictionary of key/value pairs to save to shared data. Values should be as granular as possible.
   */
  saveSharedData(campaignId, campaignStepId, moduleId, data) {
    const copy = {};
    Object.entries(data).forEach(([key, value]) => {
      copy[key] = {
        value,
        uuid_module: moduleId,
        uuid_campaign_step: campaignStepId,
      };
    });
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/`,
      merge(this.fetchOptions, {
        method: 'PUT',
        body: JSON.stringify({ shared_data: copy }),
      }),
    );
  }

  /**
   * @param {string} campaignId
   * @param {string} subCategoryId
   * @param {Array} objectIds
   * @returns {Promise}
   */
  setCampaignSharedObjects(campaignId, subCategoryId, objectIds) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/${subCategoryId}/objects/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify({ uuid_objects: objectIds, replace: true }),
      }),
    );
  }

  /**
   * @param {string} campaignStepId
   * @returns {Promise}
   */
  getStepSharedObjects(campaignId, subCategoryId) {
    return fetchJSON(
      `${this.origin}/campaign/${campaignId}/${subCategoryId}/objects/`,
      this.fetchOptions,
    );
  }

  /**
   * @param {String} campaignId - current active uuid_campaign
   * @param {String} frameworkStepId - current active uuid_framework_step
   * @param {object} data
   * @returns {Promise}
   */
  updateStep(campaignId, frameworkStepId, data) {
    return fetchJSON(
      `${this.origin}/step/${campaignId}/${frameworkStepId}/`,
      merge(this.fetchOptions, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    );
  }

  /**
   * @param {String} campaignStepId - the id of the campaign step
   * @param {Array} ids - an array of user personGUID strings
   * @returns {Promise}
   */
  updateClientUsers(campaignStepId, ids) {
    return fetchJSON(
      `${this.origin}/step/${campaignStepId}/client-users/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    );
  }

  /**
   * @param {String} campaignStepId - the id of the campaign step
   * @param {Array} ids - an array of Teams user personGUID strings
   * @returns {Promise}
   */
  updateTeamsUsers(campaignStepId, ids) {
    return fetchJSON(
      `${this.origin}/step/${campaignStepId}/teams-users/`,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    );
  }

  /**
   * @param {string} campaignId - Campaign ID requesting the campaigns within its portfolio
   * @returns {Promise}
   */
  getPortfolio(campaignId) {
    const url = `${this.origin}/portfolio/${campaignId}/`;
    return fetchJSON(url, this.fetchOptions);
  }

  /**
   * This method handles both the creation and update of a Portfolio
   * @param {string} campaignId
   * @param {Object} data
   * @returns {Promise}
   */
  updatePortfolio(campaignId, data) {
    const url = `${this.origin}/portfolio/${campaignId}/`;
    return fetchJSON(
      url,
      merge(this.fetchOptions, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    );
  }

  deletePortfolioCampaign(campaignId, mappedCampaignId) {
    const url = `${this.origin}/portfolio/${campaignId}/${mappedCampaignId}`;
    return fetchJSON(url, merge(this.fetchOptions, { method: 'DELETE' }));
  }

  /**
   * @param {string} campaignId - This is to fetch /currencies api during create campaign request
   * @returns {Promise}
   */
  async getCurrencies() {
    const url = `${this.origin}/omni_dictionary/currencies`;
    // Currencies won't change during a session; we can use a static cache
    return this.constructor.getCache(url, async () => {
      const response = await fetchJSON(url, this.fetchOptions);
      return response.sort((a, b) =>
        a?.abbreviation?.localeCompare(b?.abbreviation),
      );
    });
  }

  async getRegions() {
    const url = `${this.origin}/omni_dictionary/regions`;
    // Regions won't change during a session; we can use a static cache
    return this.constructor.getCache(url, async () => {
      const response = await fetchJSON(url, this.fetchOptions);
      return response.sort((a, b) => a?.name?.localeCompare(b?.name));
    });
  }

  async getMarkets() {
    const url = `${this.origin}/omni_dictionary/markets`;
    // Markets won't change during a session; we can use a static cache
    return this.constructor.getCache(url, async () => {
      const response = await fetchJSON(url, this.fetchOptions);
      return response.sort((a, b) => a?.name?.localeCompare(b?.name));
    });
  }

  async getCategories() {
    const url = `${this.origin}/omni_dictionary/categories`;
    // Categories won't change during a session; we can use a static cache
    return this.constructor.getCache(url, async () => {
      const response = await fetchJSON(url, this.fetchOptions);
      return response.sort((a, b) => a?.name?.localeCompare(b?.name));
    });
  }

  async getSubcategories(categoryId) {
    const url = `${this.origin}/omni_dictionary/sub-categories/${categoryId}`;
    // Sub Categories won't change during a session; we can use a static cache
    return this.constructor.getCache(url, async () => {
      const response = await fetchJSON(url, this.fetchOptions);
      return response.sort((a, b) => a?.name?.localeCompare(b?.name));
    });
  }

  /**
   * Gets name of omni-dictionary object via its UUID
   * @param {string} type - type of omni-dictionary object to fetch
   * @param {string} id - UUID of object to fetch
   * @param {string} childId - Optional UUID of child of parent dictionary object
   */
  async getDictionaryObjectName(type, id, childId) {
    const methodName = `get${type
      .replace(/^./, letter => letter.toUpperCase())
      .replace(/y$/, 'ie')}s`;
    if (!this[methodName]) throw new Error(`Unsupported type: ${type}`);
    const allObjects = await this[methodName](id);
    const match = allObjects.find(object => object.id === (childId ?? id));
    if (!match) return childId ?? id;
    return match.name;
  }

  assistCompletion(prompt, type, history) {
    const url = `${this.origin}/assist/`;
    return fetchJSON(
      url,
      merge(this.fetchOptions, {
        method: 'POST',
        body: JSON.stringify({ prompt, type, history }),
      }),
    );
  }

  async qSparksToken() {
    const url = `${this.origin}/sparks_q_ai/short_token`;
    return fetchJSON(url, this.fetchOptions);
  }
}

// jsdoc requires classes not be exported via "export default class X"
export default CampaignsAPI;
